import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export const Route = createFileRoute("/aplicativos-iptv/playsim")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o PlaySim na Samsung, LG e Roku TV" },
      { name: "description", content: "Guia completo de como baixar, instalar e configurar o PlaySim na sua Smart TV Samsung, LG ou Roku TV. Aplicativo moderno compatível com Painel IPTV Pós Pago." },
    ],
  }),
  component: PlaySimTutorial,
});

function PlaySimTutorial() {
  const tutorialSteps = [
    {
      title: "Samsung Smart TV",
      steps: [
        "Conecte sua Samsung Smart TV à internet.",
        "Abra a loja de aplicativos Samsung.",
        "Utilize a barra de pesquisa.",
        "Digite: PlaySim",
        "Selecione o aplicativo oficial.",
        "Clique em Instalar.",
        "Aguarde a conclusão da instalação.",
        "Abra o aplicativo.",
        "Informe seus dados de acesso."
      ]
    },
    {
      title: "LG Smart TV",
      steps: [
        "Conecte sua LG Smart TV à internet.",
        "Abra a LG Content Store.",
        "Utilize a busca da loja.",
        "Pesquise por: PlaySim",
        "Selecione o aplicativo.",
        "Clique em Instalar.",
        "Aguarde a instalação.",
        "Abra o aplicativo.",
        "Informe suas credenciais de acesso."
      ]
    },
    {
      title: "Roku TV",
      steps: [
        "Acesse a tela inicial da Roku TV.",
        "Entre em: Streaming Channels",
        "Utilize a busca da Roku Store.",
        "Digite: PlaySim",
        "Selecione o aplicativo oficial.",
        "Clique em: Adicionar Canal",
        "Aguarde a instalação.",
        "Abra o aplicativo.",
        "Informe seus dados de acesso."
      ]
    }
  ];

  return (
    <SEOPage
      title="Como Baixar e Instalar o PlaySim na Samsung, LG e Roku TV"
      subtitle="O PlaySim é um aplicativo moderno desenvolvido para proporcionar uma experiência prática e intuitiva em Smart TVs."
      category="Tutorial de Aplicativos"
      introParagraphs={[
        "O PlaySim é um aplicativo moderno desenvolvido para oferecer praticidade, navegação fluida e excelente compatibilidade com Smart TVs. Disponível para Samsung, LG e Roku TV, o aplicativo permite uma experiência otimizada para quem busca facilidade de acesso e alto desempenho.",
        "Neste tutorial você aprenderá como baixar e instalar o PlaySim passo a passo em sua televisão. O PlaySim é totalmente compatível com sistemas profissionais de Painel IPTV Pós Pago, sendo uma excelente opção para revendedores e usuários."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "PlaySim", href: "/aplicativos-iptv/playsim" }
      ]}
      benefits={[
        { title: "Interface Moderna", description: "Design limpo e organizado para uma navegação rápida e eficiente." },
        { title: "Fácil Instalação", description: "Disponível nas principais lojas oficiais de Smart TVs." },
        { title: "Navegação Fluida", description: "Experiência de uso rápida e sem travamentos no dia a dia." },
        { title: "Compatibilidade", description: "Funciona perfeitamente em Samsung, LG e dispositivos Roku." },
        { title: "Configuração Rápida", description: "Basta inserir suas credenciais para começar a assistir." },
        { title: "Integração Total", description: "Otimizado para funcionar com Painel IPTV Pós Pago." }
      ]}
      howItWorks={[
        { title: "Busca", description: "Procure pelo PlaySim na loja oficial de apps da sua TV." },
        { title: "Instalação", description: "Baixe e instale o aplicativo oficial em poucos segundos." },
        { title: "Login", description: "Abra o app e insira os dados fornecidos pelo seu revendedor." },
        { title: "Aproveite", description: "Tudo pronto para você desfrutar do melhor conteúdo." }
      ]}
      diferenciais={[
        { title: "Estabilidade", description: "Transmissão contínua com foco na melhor experiência do usuário." },
        { title: "Atualizações", description: "App constantemente atualizado com melhorias de segurança e performance." },
        { title: "Fácil de Usar", description: "Interface intuitiva pensada para todas as idades." },
        { title: "Suporte Especializado", description: "Compatibilidade total com nossa estrutura profissional." }
      ]}
      faq={[
        { question: "O PlaySim funciona na Samsung Smart TV?", answer: "Sim. O aplicativo está disponível para modelos Samsung compatíveis na loja oficial." },
        { question: "O PlaySim funciona na LG Smart TV?", answer: "Sim. O aplicativo pode ser instalado através da LG Content Store oficial." },
        { question: "O PlaySim funciona na Roku TV?", answer: "Sim. O aplicativo está disponível na Roku Channel Store." },
        { question: "Preciso de login para utilizar o PlaySim?", answer: "Sim. Após a instalação, insira os dados de acesso fornecidos pelo seu revendedor." },
        { question: "O PlaySim é compatível com Painel IPTV Pós Pago?", answer: "Sim. O aplicativo funciona perfeitamente com sistemas profissionais de Painel IPTV Pós Pago." }
      ]}
      sections={[
        {
          title: "Interface do PlaySim",
          type: "default",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p className="text-lg">
                  Visualize a interface profissional do PlaySim. O aplicativo foi desenhado para ser extremamente leve e rápido, garantindo que você encontre seus canais, filmes e séries favoritos em poucos cliques.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "Carregamento Instantâneo",
                    "Design Minimalista",
                    "Favoritos Personalizados",
                    "EGP Atualizado"
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                      <span className="font-bold text-[10px] uppercase tracking-wider">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute inset-0 bg-electric/10 blur-[100px] rounded-full animate-pulse-glow" />
                <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl">
                  <img 
                    src="https://wogunbzijppmeuleitjq.supabase.co/storage/v1/object/public/temp-images/fcdc24f5-9fed-4588-af9b-3a8dc5ccc41e.jpeg" 
                    alt="Interface do Aplicativo PlaySim" 
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
                </div>
              </motion.div>
            </div>
          )
        },
        {
          title: "Guia Passo a Passo por Dispositivo",
          type: "grid",
          content: (
            <>
              {tutorialSteps.map((device, idx) => (
                <div key={idx} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 h-full">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <span className="bg-electric/10 p-2 rounded-lg text-electric text-sm">{idx + 1}</span>
                    {device.title}
                  </h3>
                  <ul className="space-y-4">
                    {device.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="flex gap-3 text-muted-foreground text-sm leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-electric shrink-0 mt-1" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )
        }
      ]}
      ctaTitle="🚀 Tenha Seu Próprio Painel IPTV Pós Pago"
      ctaDescription="Comece hoje mesmo com uma estrutura profissional pronta para aumentar suas vendas e oferecer o PlaySim para seus clientes."
    />
  );
}
